import { MessagesSquare } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { getDefaultMarket } from '@/app/router';
import { Button } from '@/components/ui';
import { EventType } from '@/types';
import { sendEvent } from '@/utils/analytics';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    this.logErrorToMyService(error, errorInfo);
  }

  logErrorToMyService(error: Error, errorInfo: ErrorInfo) {
    console.error('Logged Error:', error, errorInfo);

    sendEvent(
      EventType.EXCEPTION_CAUGHT,
      {
        route: window.location.pathname,
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      error,
    );
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="relative flex h-full items-center justify-center">
          <div className="relative z-10 flex flex-col items-center pb-16">
            <h1 className="text-[128px] font-bold leading-tight">Oops!</h1>
            <p className="max-w-2xl text-center text-lg text-foreground/75">
              Something went wrong. Please refresh the page or contact support
              if the issue persists.
            </p>
            <div className="mt-12 flex gap-3">
              <HomePageLink />
              <IntercomLink />
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const HomePageLink = () => {
  const navigate = useNavigate();
  return (
    <Button
      size="lg"
      className="h-14 rounded-none font-mono text-lg uppercase tracking-wider"
      onClick={() => {
        navigate(`/trade/${getDefaultMarket()}`);
        navigate(0);
      }}
    >
      Back to homepage
    </Button>
  );
};

const IntercomLink = () => {
  const { showNewMessage } = useIntercom();
  return (
    <Button
      size="lg"
      variant="secondary"
      className="h-14 gap-3 rounded-none font-mono text-lg uppercase tracking-wider"
      onClick={() => showNewMessage('[Error Report]\n')}
    >
      <MessagesSquare size={16} />
      Contact Support
    </Button>
  );
};

export default ErrorBoundary;
